import { useId, useState } from 'react';
import { Button } from 'src/components/ui/button';
import { cn } from '@/lib/utils';
import { TaskRunTiming } from './TaskRunTiming';
import { T, useGT } from 'gt-react';
import type { TaskTimelineEntry } from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';
import { TaskRunEvidenceButton } from './TaskRunEvidenceButton';
import { taskRunOutcomeLabel } from './taskRunPresentation';

type RunEntry = Extract<TaskTimelineEntry, { kind: 'agent_run' }>;

export function TaskRunOutcomeBadge({ entry }: { entry: RunEntry }) {
  const gt = useGT();
  const outcome = entry.runSummary?.outcome;
  return (
    <Badge
      variant={
        outcome === 'failed' || entry.status === 'failed'
          ? 'destructive'
          : 'secondary'
      }
    >
      {taskRunOutcomeLabel(outcome, entry.status, gt)}
    </Badge>
  );
}

export function TaskRunSummary({
  entry,
  canInspectRuns,
}: {
  entry: RunEntry;
  canInspectRuns: boolean;
}) {
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const summaryId = useId();
  const summary = entry.runSummary;
  return (
    <div className="mt-1 flex flex-col items-start gap-2 text-sm">
      {summary?.summary ? (
        <p
          id={summaryId}
          className={cn(
            'w-full whitespace-pre-wrap break-words',
            !summaryExpanded && 'line-clamp-2',
          )}
        >
          {summary.summary}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="task timeline missing saved run summary">
            No task summary was recorded for this run.
          </T>
        </p>
      )}
      {summary?.question && (
        <p className="whitespace-pre-wrap break-words font-medium">
          {summary.question}
        </p>
      )}
      {entry.error && (
        <p className="whitespace-pre-wrap break-words text-destructive">
          {entry.error}
        </p>
      )}
      <RunSummaryControls
        entry={entry}
        canInspectRuns={canInspectRuns}
        summaryExpanded={summaryExpanded}
        summaryId={summaryId}
        onToggleSummary={() => setSummaryExpanded(!summaryExpanded)}
      />
    </div>
  );
}

function RunSummaryControls({
  entry,
  canInspectRuns,
  summaryExpanded,
  summaryId,
  onToggleSummary,
}: {
  entry: RunEntry;
  canInspectRuns: boolean;
  summaryExpanded: boolean;
  summaryId: string;
  onToggleSummary: () => void;
}) {
  const gt = useGT();
  const summary = entry.runSummary;
  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
      <TaskRunEvidenceButton
        runDetails={entry}
        stepCount={summary?.stepCount}
        agentRunId={entry.agentRunId}
        agentName={summary?.agentName}
        allowed={canInspectRuns}
        summary={summary?.summary}
        outcome={summary?.outcome}
      />
      {summary?.summary && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs font-normal"
          aria-expanded={summaryExpanded}
          aria-controls={summaryId}
          onClick={onToggleSummary}
        >
          {summaryExpanded
            ? gt('Collapse Summary', {
                context: 'collapse the full saved task run summary',
              })
            : gt('Full Summary', {
                context: 'expand the full saved task run summary',
              })}
        </Button>
      )}
      <RunErrorCount entry={entry} />
      <TaskRunTiming
        startedAt={entry.startedAt}
        completedAt={entry.completedAt}
      />
    </div>
  );
}

function RunErrorCount({ entry }: { entry: RunEntry }) {
  const gt = useGT();
  const count = entry.runSummary?.errorCount ?? 0;
  if (count === 0) return null;
  return (
    <Badge variant="destructive">
      {gt('{count} steps with errors', {
        context: 'task run errors including recovered failures',
        count: String(count),
      })}
    </Badge>
  );
}
